/**
 * App-wide validation library.
 *
 * Each validator returns either:
 *   - `null`              → value is valid
 *   - a string message    → value is invalid
 *
 * Use the small atomic validators (`required`, `minLength`, ...) to compose
 * field-level rules, or use the higher-level helpers (`validateEmail`,
 * `validatePassword`, ...) which already bundle the common checks.
 *
 * For object/form-level validation, pass a schema to `validateForm()`:
 *
 *   const errors = validateForm(values, {
 *     email:    [required("Email"), isEmail],
 *     password: [validatePassword],
 *     phone:    [(v) => validatePhone(v)],
 *   });
 *
 * `errors` is `{ field: "message" }` and is empty when everything passes.
 */

/* ------------------------------------------------------------------ */
/* Regex / charset constants                                           */
/* ------------------------------------------------------------------ */

export const PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  digitsOnly: /^\d+$/,
  alphaOnly: /^[A-Za-z]+$/,
  alphanumeric: /^[A-Za-z0-9]+$/,
  nameChars: /^[A-Za-z][A-Za-z' -]*$/, // letters + spaces + apostrophes + hyphens
  canadianPostal: /^[A-Z]\d[A-Z]\d[A-Z]\d$/,
  usZip: /^\d{5}(-\d{4})?$/,
  lowercase: /[a-z]/,
  uppercase: /[A-Z]/,
  digit: /\d/,
  special: /[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]/,
};

/* ------------------------------------------------------------------ */
/* Atomic validators                                                   */
/* ------------------------------------------------------------------ */

const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "boolean") return value === false;
  return false;
};

export const required =
  (label = "This field") =>
  (value) =>
    isEmpty(value) ? `${label} is required` : null;

export const minLength =
  (n, label = "This field") =>
  (value) => {
    if (isEmpty(value)) return null;
    return String(value).length < n
      ? `${label} must be at least ${n} characters`
      : null;
  };

export const maxLength =
  (n, label = "This field") =>
  (value) => {
    if (isEmpty(value)) return null;
    return String(value).length > n
      ? `${label} must be at most ${n} characters`
      : null;
  };

export const pattern = (regex, message) => (value) => {
  if (isEmpty(value)) return null;
  return regex.test(String(value)) ? null : message;
};

export const matches =
  (otherField, message = "Values do not match") =>
  (value, allValues = {}) =>
    value === allValues[otherField] ? null : message;

/* ------------------------------------------------------------------ */
/* Composed / domain validators                                        */
/* ------------------------------------------------------------------ */

/** Email format check (does not enforce required on its own). */
export const isEmail = (value) => {
  if (isEmpty(value)) return null;
  return PATTERNS.email.test(String(value).trim())
    ? null
    : "Please enter a valid email";
};

/** Required + format email validation. */
export const validateEmail = (value, { label = "Email" } = {}) =>
  required(label)(value) || isEmail(value);

/**
 * Strong password: 8+ chars with at least one lowercase, one uppercase,
 * one digit, and one special character.
 */
export const validatePassword = (
  value,
  { label = "Password", min = 8 } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  if (value.length < min) return `${label} must be at least ${min} characters`;
  if (!PATTERNS.lowercase.test(value))
    return `${label} must include a lowercase letter`;
  if (!PATTERNS.uppercase.test(value))
    return `${label} must include an uppercase letter`;
  if (!PATTERNS.digit.test(value)) return `${label} must include a number`;
  if (!PATTERNS.special.test(value))
    return `${label} must include a special character`;
  return null;
};

/** Confirms that `confirm` matches the canonical password. */
export const validateConfirmPassword = (
  confirm,
  password,
  { label = "Confirm Password" } = {}
) => {
  const r = required(label)(confirm);
  if (r) return r;
  return confirm === password ? null : "Passwords do not match";
};

/**
 * Phone number — digits-only, length-bounded.
 * Default range covers most international formats (E.164 allows up to 15).
 */
export const validatePhone = (
  value,
  { label = "Phone number", min = 7, max = 15 } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  const digits = String(value).replaceAll(/\D+/g, "");
  if (!PATTERNS.digitsOnly.test(digits))
    return `${label} must contain digits only`;
  if (digits.length < min) return `${label} must be at least ${min} digits`;
  if (digits.length > max) return `${label} must be at most ${max} digits`;
  return null;
};

/** Postal / ZIP code, country-aware. Accepts any non-empty string when
 *  `country` is unknown. */
export const validatePostalCode = (
  value,
  country,
  { label = "Postal code" } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  const cleaned = String(value).replaceAll(/\s+/g, "").toUpperCase();
  if (country === "CA" && !PATTERNS.canadianPostal.test(cleaned)) {
    return "Please enter a valid Canadian postal code (e.g., A1A2B2)";
  }
  if (country === "US" && !PATTERNS.usZip.test(cleaned)) {
    return "Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)";
  }
  return null;
};

/** Person name — letters, spaces, hyphens, apostrophes; length-bounded. */
export const validateName = (
  value,
  { label = "Name", min = 2, max = 50 } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  const trimmed = value.trim();
  if (trimmed.length < min) return `${label} must be at least ${min} characters`;
  if (trimmed.length > max)
    return `${label} must be at most ${max} characters`;
  if (!PATTERNS.nameChars.test(trimmed))
    return `${label} can only contain letters, spaces, hyphens, and apostrophes`;
  return null;
};

/** Generic non-empty trimmed text with optional length bounds. */
export const validateText = (
  value,
  { label = "This field", min, max } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  if (typeof min === "number") {
    const e = minLength(min, label)(value);
    if (e) return e;
  }
  if (typeof max === "number") {
    const e = maxLength(max, label)(value);
    if (e) return e;
  }
  return null;
};

/** Boolean checkbox that must be `true` (e.g., "I agree to the terms"). */
export const validateChecked = (
  value,
  { label = "This box", message } = {}
) => (value === true ? null : message || `${label} must be checked`);

/* ------------------------------------------------------------------ */
/* Form-level helpers                                                  */
/* ------------------------------------------------------------------ */

/** Run a list of validators against a value. Returns the first error or null. */
export const runValidators = (validators, value, allValues = {}) => {
  for (const v of validators) {
    const err = v(value, allValues);
    if (err) return err;
  }
  return null;
};

/**
 * Validate an object against a schema.
 *
 * @param {object} values - the form values
 * @param {object} schema - { fieldName: validator | validator[] }
 * @returns {object} `{ field: "error message" }` (empty if all pass)
 */
export const validateForm = (values, schema) => {
  const errors = {};
  for (const [field, rule] of Object.entries(schema)) {
    const list = Array.isArray(rule) ? rule : [rule];
    const err = runValidators(list, values[field], values);
    if (err) errors[field] = err;
  }
  return errors;
};

/** Convenience: returns true if `validateForm` would produce no errors. */
export const isValid = (values, schema) =>
  Object.keys(validateForm(values, schema)).length === 0;
